import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit3 } from 'lucide-react';
import { useFeaturedItems } from '@/hooks/use-featured';
import { FeaturedGridItem } from './FeaturedGridItem';
import { FeaturedEditor } from './FeaturedEditor';
import { Button } from '@/components/ui/button';

interface FeaturedGridProps {
  userId: string;
  isOwnProfile: boolean;
  isEditing?: boolean;
  onEditingChange?: (editing: boolean) => void;
}

export function FeaturedGrid({ userId, isOwnProfile, isEditing = false, onEditingChange }: FeaturedGridProps) {
  const { data: items = [] } = useFeaturedItems(userId);
  const [isEditMode, setIsEditMode] = useState(isEditing);

  // Sync external editing state
  useEffect(() => {
    setIsEditMode(isEditing);
  }, [isEditing]);

  // Grid layout configuration
  const GRID_COLS = 12;
  const GAP = 16; // px
  const ITEM_HEIGHT = 200; // base height in px

  // Convert grid coordinates to pixels for absolute positioning
  const getItemStyle = (item: any) => {
    const colWidth = `calc((100% - ${GAP * (GRID_COLS - 1)}px) / ${GRID_COLS})`;
    
    return {
      left: `calc(${item.grid_x} * (${colWidth} + ${GAP}px))`,
      top: `${item.grid_y * (ITEM_HEIGHT + GAP)}px`,
      width: `calc(${item.grid_width} * ${colWidth} + ${(item.grid_width - 1) * GAP}px)`,
      height: `${item.grid_height * ITEM_HEIGHT + (item.grid_height - 1) * GAP}px`,
    };
  };

  // Calculate container height based on items
  const containerHeight = items.length > 0
    ? Math.max(...items.map(item => (item.grid_y + item.grid_height) * (ITEM_HEIGHT + GAP)))
    : ITEM_HEIGHT;

  if (items.length === 0 && !isOwnProfile) {
    return null;
  }

  if (items.length === 0 && isOwnProfile) {
    return (
      <div className="text-center py-20">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
          <Plus className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Feature your best content</h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          Pin your favorite posts, loops, albums, and moodboards to the top of your profile
        </p>
        <Button onClick={() => {
          setIsEditMode(true);
          onEditingChange?.(true);
        }}>
          <Plus className="w-4 h-4 mr-2" />
          Add Featured Item
        </Button>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Edit Mode Toggle */}
      {isOwnProfile && !isEditMode && (
        <div className="flex justify-end mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setIsEditMode(true);
              onEditingChange?.(true);
            }}
            className="gap-2"
          >
            <Edit3 className="w-4 h-4" />
            Edit Featured
          </Button>
        </div>
      )}

      {/* Grid Container */}
      {isEditMode && isOwnProfile ? (
        <FeaturedEditor
          items={items}
          onClose={() => {
            setIsEditMode(false);
            onEditingChange?.(false);
          }}
          userId={userId}
        />
      ) : (
        <div className="relative w-full" style={{ height: `${containerHeight}px` }}>
          {items.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className="absolute"
              style={getItemStyle(item)}
            >
              <FeaturedGridItem item={item} />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
